// Comprehensive AI Agents Data - Based on beam.ai structure

export const agentCategories = [
  {
    id: 'marketing',
    name: 'Marketing & Growth',
    description: 'Automate marketing campaigns, content creation, and lead generation',
    icon: 'TrendingUp'
  },
  {
    id: 'customer-service',
    name: 'Customer Service',
    description: 'Automate customer support, inquiry handling, and service operations',
    icon: 'MessageCircle'
  },
  {
    id: 'hr-recruitment',
    name: 'Human Resources & Recruitment',
    description: 'Streamline hiring, onboarding, and HR operations',
    icon: 'Users'
  },
  {
    id: 'retail',
    name: 'Retail & E-Commerce',
    description: 'Optimize order management, inventory, and customer experience',
    icon: 'ShoppingCart'
  },
  {
    id: 'general',
    name: 'General Problem Solvers',
    description: 'Versatile agents for data entry, email, and workflow automation',
    icon: 'Zap'
  },
  {
    id: 'analytics',
    name: 'Analytics & Data Intelligence',
    description: 'AI-powered data analysis, reporting, and business intelligence',
    icon: 'BarChart'
  }
];

export const aiAgents = [
  // Customer Service Agents
  {
    id: 'customer-service-agent',
    name: 'Customer Service AI Agent',
    category: 'customer-service',
    description: 'Handle customer inquiries, order status, returns, and subscription management with 24/7 availability.',
    workflows: ['Order Status Handling', 'Product Return Requests', 'Subscription Management', 'FAQ Assistance'],
    benefits: ['Reduce response time by 70%', '24/7 availability', 'Handle 1000+ inquiries simultaneously'],
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d'
  },
  {
    id: 'billing-support-agent',
    name: 'Billing Support AI Agent',
    category: 'customer-service',
    description: 'Automate billing inquiries, payment processing, and subscription renewals.',
    workflows: ['Billing & Renewals', 'Payment Processing', 'Invoice Generation'],
    benefits: ['Reduce billing errors by 85%', 'Automated renewal management', 'Instant payment confirmation'],
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f'
  },

  // HR & Recruitment Agents
  {
    id: 'candidate-screening-agent',
    name: 'Candidate Screening AI Agent',
    category: 'hr-recruitment',
    description: 'Automate resume analysis, scoring, and candidate shortlisting.',
    workflows: ['Resume Analysis', 'Skills Matching', 'Candidate Scoring'],
    benefits: ['Screen 1000+ resumes in minutes', 'Reduce bias', 'Improve quality of hire'],
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902'
  },
  {
    id: 'interview-scheduler-agent',
    name: 'Interview Scheduler AI Agent',
    category: 'hr-recruitment',
    description: 'Coordinate interview schedules automatically across multiple calendars.',
    workflows: ['Calendar Coordination', 'Automated Reminders', 'Rescheduling Management'],
    benefits: ['Save 10+ hours per week', 'Zero scheduling conflicts', 'Automated candidate communication'],
    image: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe'
  },
  {
    id: 'payroll-calculation-agent',
    name: 'Payroll Calculation AI Agent',
    category: 'hr-recruitment',
    description: 'Automate payroll calculations, deductions, and processing.',
    workflows: ['Salary Calculation', 'Tax Deductions', 'Payment Processing'],
    benefits: ['100% accuracy', 'Automated tax compliance', 'Instant payroll reports'],
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c'
  },

  // Retail & E-Commerce Agents
  {
    id: 'order-management-agent',
    name: 'Order Management AI Agent',
    category: 'retail',
    description: 'Automate order processing, fulfillment, and status updates.',
    workflows: ['Order Processing', 'Fulfillment Coordination', 'Status Updates'],
    benefits: ['Process 5000+ orders daily', 'Real-time inventory sync', 'Automated shipping notifications'],
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d'
  },
  {
    id: 'inventory-management-agent',
    name: 'Inventory Management AI Agent',
    category: 'retail',
    description: 'Monitor stock levels, automate reordering, and prevent stockouts.',
    workflows: ['Stock Monitoring', 'Automated Reordering', 'Multi-location Sync'],
    benefits: ['Reduce stockouts by 80%', 'Optimize inventory levels', 'Automated vendor orders'],
    image: 'https://images.unsplash.com/photo-1553413077-190dd305871c'
  },
  {
    id: 'return-handling-agent',
    name: 'Return Handling AI Agent',
    category: 'retail',
    description: 'Automate return requests, processing, and refund management.',
    workflows: ['Return Authorization', 'Refund Processing', 'Exchange Coordination'],
    benefits: ['Process returns 5x faster', 'Automated quality checks', 'Customer satisfaction boost'],
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d'
  },
  {
    id: 'product-listing-agent',
    name: 'Product Listing AI Agent',
    category: 'retail',
    description: 'Create and optimize product listings across multiple channels.',
    workflows: ['Listing Creation', 'SEO Optimization', 'Multi-channel Sync'],
    benefits: ['List 1000+ products daily', 'Automated SEO optimization', 'Consistent branding'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f'
  },

  // General Problem Solvers
  {
    id: 'data-entry-agent',
    name: 'Data Entry AI Agent',
    category: 'general',
    description: 'Automate data entry, validation, and transfer across systems.',
    workflows: ['Data Extraction', 'Validation', 'System Transfer'],
    benefits: ['99.9% accuracy', '10x faster than manual entry', 'Reduce errors by 95%'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71'
  },
  {
    id: 'email-triage-agent',
    name: 'Email Triage AI Agent',
    category: 'general',
    description: 'Automatically categorize, prioritize, and route emails.',
    workflows: ['Email Categorization', 'Priority Assignment', 'Auto-routing'],
    benefits: ['Process 1000+ emails per hour', 'Intelligent routing', 'Automated responses'],
    image: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2'
  },
  {
    id: 'data-extraction-agent',
    name: 'Data Extraction AI Agent',
    category: 'general',
    description: 'Extract structured data from documents, emails, and forms.',
    workflows: ['Document Parsing', 'Data Structuring', 'Multi-format Support'],
    benefits: ['Extract from any document type', '98% accuracy', 'Instant structured output'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71'
  },
  {
    id: 'rfp-agent',
    name: 'RFP AI Agent',
    category: 'general',
    description: 'Automate RFP response creation, requirement analysis, and proposal drafting.',
    workflows: ['Requirement Analysis', 'Response Generation', 'Compliance Checks'],
    benefits: ['Respond to RFPs 5x faster', 'Consistent quality', 'Automated compliance'],
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40'
  },

  // Analytics Agents
  {
    id: 'business-intelligence-agent',
    name: 'Business Intelligence AI Agent',
    category: 'analytics',
    description: 'Automate data analysis, report generation, and insight discovery from business data.',
    workflows: ['Data Analysis', 'Report Generation', 'Insight Discovery', 'Dashboard Creation'],
    benefits: ['Generate reports 20x faster', 'Real-time insights', 'Automated data visualization'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71'
  },
  {
    id: 'predictive-analytics-agent',
    name: 'Predictive Analytics AI Agent',
    category: 'analytics',
    description: 'Forecast trends, predict outcomes, and identify patterns in business data.',
    workflows: ['Trend Forecasting', 'Pattern Recognition', 'Risk Prediction', 'Opportunity Identification'],
    benefits: ['Predict trends with 95% accuracy', 'Early risk detection', 'Data-driven decisions'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71'
  },
  {
    id: 'data-quality-agent',
    name: 'Data Quality AI Agent',
    category: 'analytics',
    description: 'Monitor, validate, and improve data quality across all systems.',
    workflows: ['Data Validation', 'Quality Monitoring', 'Error Detection', 'Data Cleansing'],
    benefits: ['99% data accuracy', 'Automated quality checks', 'Real-time monitoring'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71'
  },
  {
    id: 'customer-analytics-agent',
    name: 'Customer Analytics AI Agent',
    category: 'analytics',
    description: 'Analyze customer behavior, segment audiences, and predict churn.',
    workflows: ['Behavior Analysis', 'Customer Segmentation', 'Churn Prediction', 'Lifetime Value Calculation'],
    benefits: ['Reduce churn by 35%', 'Identify high-value segments', 'Personalized insights'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f'
  },

  // Marketing Agents
  {
    id: 'content-generation-agent',
    name: 'Content Generation AI Agent',
    category: 'marketing',
    description: 'Create marketing content, blog posts, social media posts, and ad copy automatically.',
    workflows: ['Blog Writing', 'Social Media Posts', 'Ad Copy Creation', 'Email Content'],
    benefits: ['Generate 100+ pieces daily', 'Brand-consistent content', 'SEO optimized'],
    image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7'
  },
  {
    id: 'lead-generation-agent',
    name: 'Lead Generation AI Agent',
    category: 'marketing',
    description: 'Identify, qualify, and nurture leads automatically across channels.',
    workflows: ['Lead Discovery', 'Qualification', 'Automated Outreach', 'Lead Scoring'],
    benefits: ['3x more qualified leads', 'Automated nurturing', '50% faster pipeline'],
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d'
  },
  {
    id: 'campaign-management-agent',
    name: 'Campaign Management AI Agent',
    category: 'marketing',
    description: 'Plan, execute, and optimize marketing campaigns across all channels.',
    workflows: ['Campaign Planning', 'Multi-channel Execution', 'Performance Tracking', 'Budget Optimization'],
    benefits: ['Manage 50+ campaigns simultaneously', 'Optimize ROI automatically', 'Real-time performance'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f'
  },
  {
    id: 'seo-optimization-agent',
    name: 'SEO Optimization AI Agent',
    category: 'marketing',
    description: 'Optimize content for search engines, track rankings, and identify opportunities.',
    workflows: ['Keyword Research', 'Content Optimization', 'Ranking Tracking', 'Competitor Analysis'],
    benefits: ['Increase organic traffic by 150%', 'Automated SEO audits', 'Content recommendations'],
    image: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a'
  },
  {
    id: 'social-media-agent',
    name: 'Social Media Management AI Agent',
    category: 'marketing',
    description: 'Schedule posts, engage with audiences, and analyze social media performance.',
    workflows: ['Post Scheduling', 'Engagement Automation', 'Performance Analytics', 'Trend Monitoring'],
    benefits: ['Manage 10+ platforms', '10x engagement rate', 'Automated response'],
    image: 'https://images.unsplash.com/photo-1611262588024-d12430b98920'
  },
  {
    id: 'email-marketing-agent',
    name: 'Email Marketing AI Agent',
    category: 'marketing',
    description: 'Create, send, and optimize email campaigns with personalization at scale.',
    workflows: ['Campaign Creation', 'Personalization', 'A/B Testing', 'Performance Optimization'],
    benefits: ['50% higher open rates', 'Automated personalization', 'Optimal send times'],
    image: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2'
  }
];

// Industry Solutions
export const industrySolutions = [
  {
    id: 'autonomousvehicles',
    name: 'Autonomous Vehicles',
    description: 'AI-powered navigation and decision-making for safe autonomous driving',
    agents: [],
    benefits: ['Computer vision & perception', 'Real-time decision making', 'Safe navigation systems'],
    image: 'https://datahacker.rs/wp-content/uploads/2019/08/Featured-Image-002-Top-5-autonomous-driving-systems-in-the-world.jpg'
  },
  {
    id: 'aibattery',
    name: 'AI Battery Management',
    description: 'AI-driven battery management systems for optimal performance and safety',
    agents: [],
    benefits: ['Predictive maintenance', 'Adaptive charging optimization', 'Real-time fault detection'],
    image: 'https://images.unsplash.com/photo-1593941707874-ef25b8b4a92b'
  },
  {
    id: 'logistics',
    name: 'Logistics',
    description: 'AI agents for shipment tracking, route optimization, and supply chain management',
    agents: [],
    benefits: ['Automate 80% of tracking queries', 'Route optimization', 'Inventory management'],
    image: 'https://images.unsplash.com/photo-1644079446600-219068676743'
  },
  {
    id: 'retail-ecommerce',
    name: 'Retail & E-Commerce',
    description: 'Streamline order management, inventory, and customer experience',
    agents: ['order-management-agent', 'inventory-management-agent', 'customer-service-agent'],
    benefits: ['Process 5000+ orders daily', 'Reduce stockouts by 80%', 'Increase satisfaction by 45%'],
    image: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc'
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    description: 'AI agents for production monitoring, maintenance scheduling, and quality control',
    agents: [],
    benefits: ['Reduce downtime by 50%', 'Predictive maintenance', 'Quality assurance'],
    image: 'https://images.unsplash.com/photo-1647427060118-4911c9821b82'
  }
];

// Platform Features
export const platformFeatures = [
  {
    title: 'Multi-Agent Orchestration',
    description: 'Coordinate multiple AI agents to work together on complex workflows',
    icon: 'Network'
  },
  {
    title: 'No-Code Studio',
    description: 'Build and deploy agents without coding using our visual workflow builder',
    icon: 'Layout'
  },
  {
    title: '1,500+ Integrations',
    description: 'Connect with your existing systems and tools seamlessly',
    icon: 'Link'
  },
  {
    title: 'Human-in-the-Loop',
    description: 'Maintain control with human oversight and approval workflows',
    icon: 'Users'
  },
  {
    title: 'Enterprise Security',
    description: 'SOC 2 certified, GDPR compliant, with audit logs and encryption',
    icon: 'Shield'
  },
  {
    title: 'Continuous Learning',
    description: 'Agents improve over time through feedback and monitoring',
    icon: 'TrendingUp'
  }
];
